import React, {useEffect} from 'react';
import Helmet from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import styled from 'styled-components';
import DKidsGames from '../components/DKidsGames';
import GameItem from '../components/DKidsGames/GameItem';
import DKidsLive from '../components/DKidsLive';
import DKidsScrollTopButton from '../components/DKidsScrollTopButton';
import GamesGrid from '../components/GamesGrid';
import Loader from '../components/Loader';
import TrendingRow from '../components/TrendingRow';
import {debugCNitoUrl} from '../helpers';
import {analyticsTrackState} from '../helpers/Analytics';
import {displayNextInFullScreen, loadGames} from '../store/actions/games';
import {loadLives} from '../store/actions/lives';
import {selectGames, selectLives} from '../store/reducers';
import {breakpoints, mobileMediaQuery} from '../styles';
import {getCurrentLang} from '../utils/i18n';

const DKidsHome = () => {
    const games = useSelector(selectGames);
    const lives = useSelector(selectLives);
    const {t} = useTranslation();
    const [scrollTopVisible, setScrollTopVisible] = React.useState(false);

    const dispatch = useDispatch();
    const history = useHistory();

    useEffect(() => {
        if (!games) {
            dispatch(loadGames());
        }
        if (!lives) {
            dispatch(loadLives());
        }

        analyticsTrackState('home', {
            section: 'Homepage',
            contenttype: 'Homepage',
            contenttitle: 'Homepage',
        });
        dispatch(displayNextInFullScreen(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickGame = (id, game) => {
        dispatch(displayNextInFullScreen(true));
        const link = getCurrentLang() === 'es' ? 'juegos/' : 'jogos/';
        history.push(`${link}${game.slug}${debugCNitoUrl()}`);
    };

    useEffect(() => {
        handleWindowScroll();
        window.addEventListener('scroll', handleWindowScroll);

        return () => {
            window.removeEventListener('scroll', handleWindowScroll);
        };

        // eslint-disable-next-line
    }, [window.location.pathname]);

    const handleWindowScroll = () => {
        if (window.pageYOffset < 400) {
            setScrollTopVisible(false);
        } else {
            setScrollTopVisible(true);
        }
    };

    return (
        <>
            <Helmet>
                <title>Discovery Kids</title>
            </Helmet>

            <Container>
                {(!lives || !games) && <Loader />}
                {!!lives && lives.length > 0 && <DKidsLive live={lives[0]} />}

                {!!games && (
                    <>
                        <DKidsGames
                            games={games}
                            onClickItem={handleClickGame}
                        />
                        <TrendingRow
                            data={games.slice(3, 6)}
                            onItemRender={(item, index) => {
                                return (
                                    <TrendingItem
                                        game={item}
                                        key={item.id}
                                        onClick={handleClickGame}
                                    />
                                );
                            }}
                        />
                        <SectionTitle>{t('games.allGames')}</SectionTitle>
                        <CustomGrid
                            items={[...games.slice(3), ...games.slice(0, 3)]}
                            hidePlayButton
                            onClickItem={handleClickGame}
                            brand={'dkids'}
                        />
                        {!!lives && lives.length > 0 && (
                            <DKidsScrollTopButton
                                visible={scrollTopVisible}
                                live={lives[0]}
                            />
                        )}
                    </>
                )}
            </Container>
        </>
    );
};

const Container = styled.div`
    width: 100%;
    height: 100%;
    background-color: #4757ff;
    min-height: 800px;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const TrendingItem = styled(GameItem)`
    width: 100%;
    height: 100%;
`;

const CustomGrid = styled(GamesGrid)`
    max-width: 1360px;
    align-self: center;
    margin-bottom: 40px;
`;

const SectionTitle = styled.div`
    font-family: Mont;
    font-weight: 900;
    font-style: normal;
    font-size: 20px;
    line-height: 26px;
    text-transform: uppercase;
    margin: 16px 0px 16px 40px;

    align-self: flex-start;
    ${mobileMediaQuery} {
        margin: 16px 0px 16px 25px;
    }

    @media screen and (min-width: ${breakpoints.xl}px) {
        align-self: center;
        max-width: 1360px;
        margin: 16px 0px;
        text-align: left;
        width: 100%;
    }
`;

export default DKidsHome;
